import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { generatePDFReport, generateCSVReport } from '../../../services/exportService';
import { format, subDays, subMonths, subYears } from 'date-fns';

const ExportOptions = ({ reportFilters }) => {
  const { user } = useAuth();
  const [exportFormat, setExportFormat] = useState('pdf');
  const [selectedMetrics, setSelectedMetrics] = useState(['activity', 'nutrition', 'sleep']);
  const [dateRange, setDateRange] = useState({
    preset: 'last30days',
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [isExporting, setIsExporting] = useState(false);
  const [includeOptions, setIncludeOptions] = useState({
    summary: true,
    charts: true,
    rawData: true
  });

  const formatOptions = [
    { value: 'pdf', label: 'PDF Report', icon: 'FileText' },
    { value: 'csv', label: 'CSV Data', icon: 'Table' }
  ];

  const metricOptions = [
    { id: 'activity', label: 'Activity & Workouts', description: 'Exercise logs and completion rates' },
    { id: 'nutrition', label: 'Nutrition & Calories', description: 'Meal logs and macronutrient data' },
    { id: 'sleep', label: 'Sleep Analysis', description: 'Sleep duration and quality metrics' }
  ];

  const dateRangePresets = [
    { value: 'last7days', label: 'Last 7 Days', days: 7 },
    { value: 'last30days', label: 'Last 30 Days', days: 30 },
    { value: 'last3months', label: 'Last 3 Months', months: 3 },
    { value: 'last6months', label: 'Last 6 Months', months: 6 },
    { value: 'lastyear', label: 'Last Year', years: 1 },
    { value: 'custom', label: 'Custom Range', custom: true }
  ];

  const handleDateRangeChange = (preset) => {
    const today = new Date();
    let startDate, endDate;

    const selectedPreset = dateRangePresets?.find(p => p?.value === preset);

    if (selectedPreset?.days) {
      startDate = format(subDays(today, selectedPreset?.days), 'yyyy-MM-dd');
      endDate = format(today, 'yyyy-MM-dd');
    } else if (selectedPreset?.months) {
      startDate = format(subMonths(today, selectedPreset?.months), 'yyyy-MM-dd');
      endDate = format(today, 'yyyy-MM-dd');
    } else if (selectedPreset?.years) {
      startDate = format(subYears(today, selectedPreset?.years), 'yyyy-MM-dd');
      endDate = format(today, 'yyyy-MM-dd');
    } else if (preset === 'custom') {
      startDate = customDateRange?.startDate;
      endDate = customDateRange?.endDate;
    }

    setDateRange({ preset, startDate, endDate });
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({ ...prev, [field]: value }));
    if (dateRange?.preset === 'custom') {
      setDateRange(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleMetricToggle = (metricId, checked) => {
    setSelectedMetrics(prev => 
      checked 
        ? [...prev, metricId]
        : prev?.filter(id => id !== metricId)
    );
  };

  const handleExport = async () => {
    if (!user?.id) {
      alert('Please log in to export reports');
      return;
    }

    if (selectedMetrics?.length === 0) {
      alert('Please select at least one metric to export');
      return;
    }

    setIsExporting(true);
    
    try {
      const exportOptions = {
        dateRange: {
          startDate: dateRange?.startDate,
          endDate: dateRange?.endDate
        },
        selectedMetrics,
        includeCharts: includeOptions?.charts,
        includeSummary: includeOptions?.summary,
        includeRawData: includeOptions?.rawData
      };

      let result;
      if (exportFormat === 'pdf') {
        result = await generatePDFReport(user?.id, exportOptions);
      } else if (exportFormat === 'csv') {
        result = await generateCSVReport(user?.id, exportOptions);
      }

      if (result?.success) {
        alert(`Report exported successfully as ${result?.fileName}`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again or check if you have data for the selected period.');
    } finally {
      setIsExporting(false);
    }
  };

  const getFormatDescription = () => {
    const descriptions = {
      pdf: 'Professional report with charts and formatting, perfect for sharing or printing.',
      csv: 'Raw data in spreadsheet format, ideal for further analysis in Excel or Google Sheets.'
    };
    return descriptions?.[exportFormat] || '';
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2 border border-border/40">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
          <Icon name="Download" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Export Options</h3>
          <p className="text-sm text-muted-foreground">Download your wellness report with customizable options</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Export Format</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {formatOptions?.map((format) => (
              <button
                key={format?.value}
                onClick={() => setExportFormat(format?.value)}
                className={`
                  flex items-center space-x-3 p-4 rounded-lg border transition-all duration-200 touch-target
                  ${
                    exportFormat === format?.value
                      ? 'border-primary bg-primary/5 text-primary' :'border-border/40 hover:border-border text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name={format?.icon} size={20} />
                <span className="font-medium">{format?.label}</span>
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-2">{getFormatDescription()}</p>
        </div>

        {/* Date Range Selection */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Date Range</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {dateRangePresets?.map((preset) => (
              <button
                key={preset?.value}
                onClick={() => handleDateRangeChange(preset?.value)}
                className={`
                  px-3 py-2 rounded-lg border text-sm transition-all duration-200
                  ${
                    dateRange?.preset === preset?.value
                      ? 'border-primary bg-primary/5 text-primary font-medium' :'border-border/40 hover:border-border text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                {preset?.label}
              </button>
            ))}
          </div>

          {dateRange?.preset === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                <input
                  type="date"
                  value={customDateRange?.startDate}
                  onChange={(e) => handleCustomDateChange('startDate', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
                <input
                  type="date"
                  value={customDateRange?.endDate}
                  onChange={(e) => handleCustomDateChange('endDate', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div className="mt-2 text-xs text-muted-foreground">
            Selected period: {format(new Date(dateRange?.startDate), 'PP')} - {format(new Date(dateRange?.endDate), 'PP')}
          </div>
        </div>

        {/* Metric Selection */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Select Metrics</h4>
          <div className="space-y-3">
            {metricOptions?.map((metric) => (
              <div key={metric?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/30 transition-colors">
                <Checkbox
                  checked={selectedMetrics?.includes(metric?.id)}
                  onChange={(e) => handleMetricToggle(metric?.id, e?.target?.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {metric?.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">{metric?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PDF-specific options */}
        {exportFormat === 'pdf' && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Include in PDF</h4>
            <div className="space-y-2">
              <Checkbox
                label="Executive Summary"
                checked={includeOptions?.summary}
                onChange={(e) => setIncludeOptions(prev => ({ ...prev, summary: e?.target?.checked }))}
              />
              <Checkbox
                label="Data Tables"
                checked={includeOptions?.rawData}
                onChange={(e) => setIncludeOptions(prev => ({ ...prev, rawData: e?.target?.checked }))}
              />
            </div>
          </div>
        )}

        {/* Export Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border/40">
          <Button
            variant="default"
            onClick={handleExport}
            loading={isExporting}
            disabled={selectedMetrics?.length === 0 || isExporting}
            iconName="Download"
            iconPosition="left"
            className="flex-1"
          >
            {isExporting ? 'Generating Report...' : `Export ${exportFormat?.toUpperCase()}`}
          </Button>
        </div>

        {/* Info Message */}
        <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Your report will include data from {format(new Date(dateRange?.startDate), 'PP')} to {format(new Date(dateRange?.endDate), 'PP')} for the selected metrics.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;